import React, { useEffect, useState } from 'react';
import { JsonpathGroup, jsonpathGroupGetAPI } from 'admin/api/jsonpathGroups';
import PfDialog from 'shared/components/atoms/PfDialog';
import RoundedButton from 'shared/components/atoms/RoundedButton';
import SelectBox from 'shared/components/atoms/SelectBox';
import AlertDialog from 'shared/components/molecules/AlertDialog';
import CheckBoxWithText from 'shared/components/molecules/CheckBoxWithText';
import InputComponent from 'shared/components/molecules/InputComponent';

import validateCsvRows from 'user/utils/validateCsvRows';
import CSVTable from './CSVTable';
import { CsvRow } from 'shared/models/CsvRow';
import { jsonpathGroupCategoryToText } from 'shared/models/JsonpathGroupCategory';
import Spinner from 'shared/components/atoms/Spinner';

interface CsvRowsInputDialogProps {
  is_open: boolean;
  value: string;
  stream_id?: string;
  stream_data_number?: number;
  onClose: (value?: string) => void;
}
interface CsvRowsInputDoalogState {
  csv_rows: CsvRow[];
  export_csv_statistics?: string;
}
/** アプリパラメータでCSV行定義を設定するダイアログです */
export default class CsvRowsInputDialog extends React.PureComponent<CsvRowsInputDialogProps, CsvRowsInputDoalogState> {
  constructor(props: CsvRowsInputDialogProps) {
    super(props);
    let o = [];
    let export_csv_statistics = 'NONE';
    try {
      o = JSON.parse(this.props.value);
      if (!Array.isArray(o)) {
        export_csv_statistics = o['export_csv_statistics'];
        o = o['csv_rows'];
      }
    } catch (e) {
      console.log(e);
    }
    this.state = {
      csv_rows: o,
      export_csv_statistics: export_csv_statistics,
    };
  }
  handleExportCsvStatistics = (value: string) => {
    this.setState({ export_csv_statistics: value });
  };
  handleCsvTableChange = (csv_rows: CsvRow[]) => {
    this.setState({ csv_rows: csv_rows });
  };
  /**
   * 入力チェックを行い、エラーの場合はメッセージを含む例外をスローします。
   */
  validate = () => {
    // csv_rowsのバリデーションでエラーを返す
    validateCsvRows(this.state.csv_rows, true);
  };
  handleOKClick = () => {
    try {
      this.validate();
    } catch (e) {
      if (e instanceof Error) {
        AlertDialog.show(e.message);
      }
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const csv_info: Record<string, any> = {
      csv_rows: this.state.csv_rows,
      export_csv_statistics: this.state.export_csv_statistics,
    };
    this.props.onClose(JSON.stringify(csv_info));
  };
  render() {
    return (
      <PfDialog
        isOpen={this.props.is_open}
        title='CSV行定義'
        onClose={() => this.props.onClose()}
        contentProps={{ style: { display: 'flex', flexDirection: 'column' } }}
        large
      >
        <InputComponent text='集計'>
          <CheckBoxWithText
            text='集計する'
            checked={this.state.export_csv_statistics !== 'NONE'}
            onClick={() => this.handleExportCsvStatistics(this.state.export_csv_statistics !== 'NONE' ? 'NONE' : 'KEY')}
          />
        </InputComponent>
        <InputComponent text='CSV定義' required>
          <CSVTable
            bodies={this.state.csv_rows}
            show_statistics={this.state.export_csv_statistics === 'KEY'}
            setCsvRows={this.handleCsvTableChange}
          />
        </InputComponent>
        <div style={{ textAlign: 'center' }}>
          <RoundedButton
            text='キャンセル'
            is_white={true}
            is_margin_right={true}
            onClick={() => this.props.onClose()}
          />
          <RoundedButton text='OK' onClick={this.handleOKClick} />
        </div>
      </PfDialog>
    );
  }
}

interface CsvRowsInputDialogButtonProps {
  value: string;
  streamId?: string;
  streamDataNumber?: number;
  onChange: (value: string) => void;
}
interface CsvRowsInputDialogButtonState {
  isOpen: boolean;
}
/** CSV定義ダイアログを開くためのボタンです。 */
export class CsvRowsInputDialogButton extends React.PureComponent<
  CsvRowsInputDialogButtonProps,
  CsvRowsInputDialogButtonState
> {
  constructor(props: CsvRowsInputDialogButtonProps) {
    super(props);
    this.state = {
      isOpen: false,
    };
  }
  private handleOpen = () => {
    this.setState({ isOpen: true });
  };
  private handleClose = (value?: string) => {
    this.setState({ isOpen: false });
    if (value !== undefined) {
      this.props.onChange(value);
    }
  };
  render() {
    return (
      <>
        <RoundedButton text='CSV定義' onClick={this.handleOpen} />
        {this.state.isOpen && (
          <CsvRowsInputDialog
            is_open={true}
            value={this.props.value}
            stream_id={this.props.streamId}
            stream_data_number={this.props.streamDataNumber}
            onClose={this.handleClose}
          />
        )}
      </>
    );
  }
}

/** JSONパスの入力ストリームの入力欄です */
interface CsvRowsInputDialogSelectButtonProps {
  value: string;
  onChange: (value: string) => void;
  display?: string;
}
export function InputJsonpathGroup(props: CsvRowsInputDialogSelectButtonProps) {
  const [jsonpath_groups, setJsonpathGroups] = useState<JsonpathGroup[]>();
  const getJsonpathGroups = async () => {
    let items: JsonpathGroup[] = [];
    let has_next = true;
    while (has_next) {
      const res = await jsonpathGroupGetAPI({
        display: props.display,
      });
      has_next = false;
      if (res.status === 200) {
        items = items.concat(res.data.items);
        has_next = res.data.has_next;
      }
    }
    setJsonpathGroups(items);
  };
  // 選択を変更したときのイベントハンドラ
  const handleChange = (value: string) => {
    props.onChange(value);
  };

  // -- onload function --
  useEffect(() => {
    // プロセス情報の取得
    getJsonpathGroups();
  }, []); /* eslint-disable-line */
  return (
    <>
      {jsonpath_groups ? (
        <SelectBox
          style={{ width: 500 }}
          datas={jsonpath_groups.map((e) => {
            return {
              name: e.jsonpath_group_name + ' [' + jsonpathGroupCategoryToText(e.category) + ']',
              value: e.jsonpath_group_id,
            };
          })}
          value={props.value}
          onChange={(e) => handleChange(e.currentTarget.value)}
        />
      ) : (
        <Spinner />
      )}
    </>
  );
}
